(ns practitest.frontend.common.format-utils
  (:require
    [cljs.pprint :refer [cl-format]]
    [clojure.string :as str]
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.common.uri :refer [set-query-param]]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]
    [tick.core :as t]
    [tick.locale-en-us]
    [tick.timezone]))

(defn fixed-point [n precision]
  (cl-format nil (str "~," precision "f") n))

(defn format-integer [number padding]
  (cl-format nil (str "~" padding ",'0D") number))

(def date-time-formatter (t/formatter "dd-MMM-yyyy HH:mm"))
(def local-date-formatter (t/formatter "dd-MMM-yyyy"))

(defn- parse-timestamp [ts]
  (if (t/instant? ts)
    ts
    (when (and ts (string? ts))
      (let [trimmed (str/trim ts)]
        (cond
          ;; We can't parse strings less than 10 chars
          (< (count trimmed) 10)
          nil

          ; "2022-01-01" - localdate format
          (= 10 (count trimmed))
          (t/date trimmed)

          ; Handle timezone offsets like "2024-03-01T04:00:00+02:00"
          ; Convert to instant via zoned-date-time
          (re-find #"[+-]\d{2}:\d{2}$" trimmed)
          (t/instant (t/zoned-date-time trimmed))

          ; Try to parse as instant
          :else
          (t/instant trimmed))))))

(defn formatted-date-time [object]
  (try
    (when object
      (when-let [parsed (if (string? object)
                          (parse-timestamp object)
                          object)]
        (let [user-timezone @(re-frame/subscribe [::subs/current-user-timezone])
              instant (if (t/date? parsed)
                        (t/instant (t/at parsed "00:00"))
                        (t/instant parsed))]
          (if user-timezone
            (let [zone (t/zone user-timezone)
                  zoned-dt (t/in instant zone)]
              (t/format date-time-formatter zoned-dt))
            ;; Fallback to system timezone if no user timezone
            (t/format date-time-formatter (t/zoned-date-time instant))))))
    (catch js/Error e
      (js/console.warn e "error formatting date time")
      "ERROR IN DATETIME")))

(defn formatted-local-date [object]
  (try
    (when object
      ;; We support both instant/localdate here
      ;; It would work like this (for GMT+5 tz):
      ;; '2022-01-02' -> "02-Jan-2022"
      ;; '2022-01-02T23:00:00Z' -> "03-Jan-2022" (because timestamp is UTC, but local date is GMT+5)
      (when-let [parsed (if (string? object)
                          (parse-timestamp object)
                          object)]
        (t/format local-date-formatter
                  (if (t/date? parsed)
                    parsed
                    (t/zoned-date-time parsed)))))
    (catch js/Error e
      (js/console.warn e "error formatting local date")
      "ERROR IN DATE")))

(defn substring-search? [value substring]
  (and (some? value)
       (some? substring)
       (let [value-str     (str/lower-case (str value))
             substring-str (str/lower-case (str substring))]
         (str/includes? value-str substring-str))))

;; Non-breaking space
(def nbsp "\u00a0")
;; Zero-width space
(def zwsp "\u200b")

(defn try-parse-number2
  "Returns `not-parsed` in case of parsing errors/string not satisfying numeric regex"
  [s not-parsed]
  (if (string? s)
    (try
      (cond
        (re-matches #"\d+" s)       (js/parseInt s)
        (str/includes? s ".")       (let [n (js/parseFloat s)]
                                      (if (js/isNaN n) not-parsed n))
        :else                       not-parsed)
      (catch js/Error e
        not-parsed))
    not-parsed))

(defn try-parse-float
  "Returns `not-parsed` in case of parsing errors/string not satisfying double digit regex, or if s is not a string"
  [s not-parsed]
  (if (string? s)
    (try
      (if (re-matches #"^[-+]?[0-9]*\.?[0-9]+([eE][-+]?[0-9]+)?$" s)
        (js/parseFloat s)
        not-parsed)
      (catch js/Error e
        not-parsed))
    not-parsed))

(defn try-parse-number
  "Returns nil in case of errors. Parses integers only !. Returns nil if s is not a string."
  [s]
  (try-parse-number2 s nil))

;; Adapted from old pt-frontend
(defn valid-duration? [s]
  (boolean (re-matches #"^\d{1,2}:\d{1,2}:\d{1,2}$" s)))

(defn format-duration [duration]
  (if duration
    (let [seconds (mod (t/seconds duration) 60)
          minutes (mod (t/minutes duration) 60)
          hours   (t/hours duration)]
      (cl-format nil "~2,'0d:~2,'0d:~2,'0d"
                 hours
                 minutes
                 seconds))
    "00:00:00"))

(defn parse-duration [s]
  (cond
    (number? s)
    (t/new-duration s :seconds)

    (string? s)
    (if (str/includes? s ":")
      (let [[hours minutes seconds] (str/split s #":" 3)]
        (t/new-duration
          (+ (js/parseInt seconds)
             (* 60 (js/parseInt minutes))
             (* 3600 (js/parseInt hours)))
          :seconds))
      (t/new-duration (js/parseInt s) :seconds))

    :else
    (t/new-duration 0 :seconds)))

(defn format-time-ago
  [timestamp]
  (when-let [inst (parse-timestamp timestamp)]
    ;; TODO: use server time somehow ?
    (let [now (t/now)
          ;; Duration
          duration (t/between inst now)
          ;; Period
          period (t/between (t/date inst) (t/date now))]
      ;; TODO: update from future ?
      (if (t/> inst now)
        "Just now"
        ;; Usual formatting
        (let [seconds (t/seconds duration)
              minutes (t/minutes duration)
              hours (t/hours duration)
              days (t/days duration)
              weeks (int (/ days 7))
              months (t/months period)
              years (t/years period)]
          [seconds minutes hours days weeks months years]
          (cond
            (> years 0)
            ;; TODO: localisation and plurals
            (str years " years ago")

            (> months 0)
            (str months " months ago")

            (> weeks 0)
            (str weeks " weeks ago")

            (> days 0)
            (str days " days ago")

            (> hours 0)
            (str hours " hours ago")

            (> minutes 0)
            (str minutes " minutes ago")

            :else
            (str " less than a minute ago")))))))

;; copy-pasted from common_entity.promptNewValue
(defn validate-new-list-value! [str]
  ;; js/alert return nil
  (cond
    (str/blank? str)
    (js/alert "You can't add an empty value")

    (> (count str) 60)
    (js/alert "Value cannot be more than 60 characters")

    (str/includes? str "<")
    (js/alert "Values may not include less-then sign (<)")

    (str/includes? str ";")
    (js/alert "Values may not include semicolons (;)")

    (str/includes? str ",")
    (js/alert "Values may not include commas (,)")

    :else
    str))

;; TODO: port ruby version
(defn format-file-size
  [val]
  (when val
    (cond
      (> val (* 1024 1024))
      (str (.toLocaleString (/ val (* 1024 1024))) nbsp "Mb")
      (> val 1024)
      (str (.toLocaleString (/ val 1024)) nbsp "Kb")
      :else
      (str (.toLocaleString val) nbsp "b"))))

(defn truncate-text [s max-len]
  (when s
    (let [len        (count s)
          overflown? (> len max-len)]
      (if overflown?
        (str (subs s 0 max-len) "...")
        s))))

(defn has-enough-text?
  "Check if text has enough content to be considered meaningful.
  Returns true if text has at least 30 characters."
  [text]
  (when text
    (let [trimmed (str/trim text)]
      (>= (count trimmed) 30))))

;; This function tries to make some sense out of rails-formatted error response
;; TODO: replace with better implementation on backend
(defn format-server-error-message
  [errors]
  (let [base (:base errors)]
    (str/join "<br/>"
              (remove nil?
                      (concat
                        [(when (seq base)
                           ;; Special "base" field for top-level errors and Custom fields
                           (str/join "<br/>" base))]

                        ;; Errors for every other field lives here
                        (map
                          (fn [[field errors]]
                            (str (name field) ": " (str/join ", " errors)))
                          (seq (dissoc errors :base))))))))

(defn format-markdown3-attachment-url [{:keys [url name type]}]
  (if (str/starts-with? type "image/")
    (str "![" name "](" (set-query-param url "style" "thumb") ")")
    (str "[" name "](" url ")")))

(defn user-initials
  "Extract initials from display name. 
  
  Display names can be arbitrary strings, but, by default, if user did not customize it, look like email or 'first-name last-name'.
  See `fix_display_name` in `User` model for more information.
  
  Returns one or two uppercase letters.
  
  Examples:
  ```clojure
  (user-initials \"Itai Cohen\") ;; => \"IC\"
  (user-initials \" joel.monte@gmail.com\") ;; => \"JM\"
  (user-initials \"Itai\") ;; => \"I\"
  (user-initials nil) ;; => \"\"
  ```
  "
  [display-name]
  (let [s (-> display-name (str) (str/trim))
        email? (re-find #"^[^@\s]+@[^@\s]+$" s)
        source (if email? (first (str/split s #"@")) s)]
    (->> (str/split source #"[\s,._\-\/\+]+")
         (remove str/blank?)
         (take 2)
         (map #(subs % 0 1))
         (str/join "")
         (str/upper-case))))

(defn format-smartfox-suffix
  [user-string smartfox?]
  (when user-string
    (str user-string
         (when smartfox?
           (str " "
                (get-translation "generic.smartfox.smartfox-used-suffix"
                                 "(With Step Generator’s assistance)"))))))

(comment

  (user-initials "Itai Cohen")
  (user-initials " Itai Cohen")
  (user-initials "stask@practitest.com")
  (user-initials nil)
  (user-initials "foo.bar@example.com")
  (user-initials "  (blah)  "))
